import React, { ChangeEvent, FC, useCallback, useContext, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { useTranslation } from 'react-i18next';
import { apiOrganizationProduct } from '../../resources/api/apiOrganizationProduct';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { OrganizationProductsFetcher } from './CustomerFetchers';

interface IProps {
  open: boolean;
  closeDialog: () => void;
  productIds: number[];
}

const { useUpdateOrganizationProducts } = apiOrganizationProduct;

export const CustomerProductBulkEdit: FC<IProps> = ({ open, closeDialog, productIds }) => {
  const { organization } = useContext(OrganizationContext);
  const { onRefresh } = useContext(OrganizationProductsFetcher.Context);
  const handleError = useApiErrorHandler();
  const { t } = useTranslation();

  const { execute: updateProducts } = useUpdateOrganizationProducts(organization ?? void 0);

  const [notificationSettings, setNotificationSettings] = useState({
    notification_reneval_270_days: null as boolean | null,
    notification_reneval_180_days: null as boolean | null,
    notification_reneval_90_days: null as boolean | null,
    notification_reneval_60_days: null as boolean | null,
    notification_reneval_30_days: null as boolean | null,
    notification_expiring_30_days: null as boolean | null,
    ignore_notification_until_reneval: null as boolean | null,
  });
  const [dealId, setDealId] = useState<string>('');

  useEffect(() => {
    if (open) {
      setNotificationSettings({
        notification_reneval_270_days: null,
        notification_reneval_180_days: null,
        notification_reneval_90_days: null,
        notification_reneval_60_days: null,
        notification_reneval_30_days: null,
        notification_expiring_30_days: null,
        ignore_notification_until_reneval: null,
      });
      setDealId('');
    }
  }, [open]);

  const handleCheckboxChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setNotificationSettings((prevState) => {
      const key = name as keyof typeof prevState;
      const currentValue = prevState[key];
      return { ...prevState, [name]: currentValue === null ? true : !currentValue };
    });
  }, []);

  const hasChanges = Object.values(notificationSettings).some((value) => value !== null) || dealId.trim() !== '';

  const saveChanges = useCallback(async () => {
    try {
      const properties = Object.fromEntries(Object.entries(notificationSettings).filter(([, value]) => value !== null));

      await updateProducts({
        productIds,
        properties,
        dealId: dealId || undefined,
      });
      await onRefresh();
      closeDialog();
    } catch (e) {
      handleError(e);
    }
  }, [updateProducts, productIds, notificationSettings, dealId, closeDialog, handleError, onRefresh]);

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>
        {t('customerProduct.notificationEditDialog.title')} ({productIds.length}{' '}
        {t('customerProduct.bulkEditDialog.products')})
      </DialogTitle>
      <DialogContent>
        <Typography paragraph>{t('customerProduct.bulkEditDialog.message', { count: productIds.length })}</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.notification_reneval_270_days === true}
              indeterminate={notificationSettings.notification_reneval_270_days === null}
              onChange={handleCheckboxChange}
              name="notification_reneval_270_days"
            />
          }
          label={t('customerProduct.notificationEditDialog.notification_reneval_270_days')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.notification_reneval_180_days === true}
              indeterminate={notificationSettings.notification_reneval_180_days === null}
              onChange={handleCheckboxChange}
              name="notification_reneval_180_days"
            />
          }
          label={t('customerProduct.notificationEditDialog.notification_reneval_180_days')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.notification_reneval_90_days === true}
              indeterminate={notificationSettings.notification_reneval_90_days === null}
              onChange={handleCheckboxChange}
              name="notification_reneval_90_days"
            />
          }
          label={t('customerProduct.notificationEditDialog.notification_reneval_90_days')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.notification_reneval_60_days === true}
              indeterminate={notificationSettings.notification_reneval_60_days === null}
              onChange={handleCheckboxChange}
              name="notification_reneval_60_days"
            />
          }
          label={t('customerProduct.notificationEditDialog.notification_reneval_60_days')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.notification_reneval_30_days === true}
              indeterminate={notificationSettings.notification_reneval_30_days === null}
              onChange={handleCheckboxChange}
              name="notification_reneval_30_days"
            />
          }
          label={t('customerProduct.notificationEditDialog.notification_reneval_30_days')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.notification_expiring_30_days === true}
              indeterminate={notificationSettings.notification_expiring_30_days === null}
              onChange={handleCheckboxChange}
              name="notification_expiring_30_days"
            />
          }
          label={t('customerProduct.notificationEditDialog.notification_expiring_30_days')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={notificationSettings.ignore_notification_until_reneval === true}
              indeterminate={notificationSettings.ignore_notification_until_reneval === null}
              onChange={handleCheckboxChange}
              name="ignore_notification_until_reneval"
            />
          }
          label={t('customerProduct.notificationEditDialog.ignore_notification_until_reneval')}
        />
        <Grid container sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <TextField
              label={t('customerProduct.notificationEditDialog.dealId')}
              value={dealId}
              onChange={(e) => setDealId(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>{t('common.dialog.close')}</Button>
        <Button variant="contained" color="primary" onClick={saveChanges} disabled={!hasChanges}>
          {t('customerProduct.notificationEditDialog.update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
